/**
 * Compact embedding pipeline progress card.
 *
 * Renders inline in the event stream to show the current embedding phase,
 * chunk progress, and model information. Designed to match the existing
 * phase timeline box pattern from EventStreamPane.
 */
import type { FC } from "react";

import type { EnrichedProviderProgressEvent } from "./eventDisplay";

const PHASES = ["discover", "chunk", "embed", "store"] as const;

const PHASE_LABELS: Record<string, string> = {
  chunk: "Chunk",
  discover: "Discover",
  embed: "Embed",
  store: "Store",
};

function getPhaseIndex(phase: string): number {
  for (let i = 0; i < PHASES.length; i++) {
    if (PHASES[i] === phase) {
      return i;
    }
  }
  return -1;
}

function resolvePhaseBoxStyle(active: boolean, completed: boolean): string {
  if (active) {
    return "animate-pulse border border-cyan-400 bg-cyan-500 text-white dark:bg-cyan-400 dark:text-cyan-950";
  }
  if (completed) {
    return "border border-emerald-400/50 bg-emerald-500/15 text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-400";
  }
  return "border border-slate-200 text-slate-400 dark:border-slate-600 dark:text-slate-500";
}

interface EmbeddingProgressCardProps {
  event: EnrichedProviderProgressEvent;
}

export const EmbeddingProgressCard: FC<EmbeddingProgressCardProps> = ({
  event,
}) => {
  const data = event.event_data;
  const phase = typeof data.phase === "string" ? data.phase : "discover";
  const filesDiscovered =
    typeof data.filesDiscovered === "number" ? data.filesDiscovered : null;
  const totalChunks =
    typeof data.totalChunks === "number" ? data.totalChunks : null;
  const chunksEmbedded =
    typeof data.chunksEmbedded === "number" ? data.chunksEmbedded : null;
  const model = typeof data.model === "string" ? data.model : null;
  const isComplete = phase === "complete";
  const currentPhaseIndex = isComplete ? PHASES.length : getPhaseIndex(phase);
  let percent = 0;
  if (totalChunks && totalChunks > 0 && chunksEmbedded !== null) {
    percent = Math.round((chunksEmbedded / totalChunks) * 100);
  } else if (isComplete) {
    percent = 100;
  }

  return (
    <div className="border-l-2 border-cyan-500 bg-cyan-50/50 p-3 dark:border-cyan-400/40 dark:bg-cyan-950/20">
      {/* Phase boxes */}
      <div className="mb-2 flex items-center gap-1.5">
        {PHASES.map((p, index) => {
          const visited = index < currentPhaseIndex;
          const active = index === currentPhaseIndex && !isComplete;
          return (
            <div key={p} className="flex items-center gap-1.5">
              <div
                className={`flex h-5 items-center px-1.5 font-mono text-[9px] font-bold uppercase tracking-widest transition-colors ${resolvePhaseBoxStyle(active, visited || isComplete)}`}
              >
                {PHASE_LABELS[p] ?? p}
              </div>
              {index < PHASES.length - 1 && (
                <div
                  className={`h-px w-2 ${
                    index < currentPhaseIndex
                      ? "bg-emerald-400 dark:bg-emerald-500/50"
                      : "bg-slate-200 dark:bg-slate-700"
                  }`}
                />
              )}
            </div>
          );
        })}
        {isComplete && (
          <span className="ml-1 font-mono text-[9px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            Done
          </span>
        )}
      </div>

      {/* Progress bar */}
      {(phase === "embed" || isComplete) && (
        <div className="mb-1.5">
          <div className="h-1 w-full overflow-hidden rounded-none bg-slate-200 dark:bg-slate-700">
            <div
              className={`h-full rounded-none transition-all duration-300 ${
                isComplete
                  ? "bg-emerald-500 dark:bg-emerald-400"
                  : "bg-cyan-500 dark:bg-cyan-400"
              }`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-3 font-mono text-[10px] text-slate-500 dark:text-slate-400">
        {filesDiscovered !== null && <span>{filesDiscovered} files</span>}
        {totalChunks !== null && (
          <span>
            {chunksEmbedded !== null && phase === "embed"
              ? `${chunksEmbedded}/${totalChunks} chunks`
              : `${totalChunks} chunks`}
          </span>
        )}
        {percent > 0 && phase === "embed" && (
          <span className="text-cyan-600 dark:text-cyan-400">{percent}%</span>
        )}
        {model && (
          <span className="ml-auto text-slate-400 dark:text-slate-500">
            {model}
          </span>
        )}
      </div>
    </div>
  );
};
